import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { MagnifyingGlass } from '@phosphor-icons/react';
import { MenuIcon, MenuIconName, PageHeader } from '@/components/core';
import { Card } from '@/components/ui-kit/card';
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui-kit/empty';
import { Input } from '@/components/ui-kit/input';
import { SETTINGS_SCOPES } from '@/constant/settings-menu';
import { usePermittedSidebar } from '@/hooks/use-permitted-sidebar';
import { SoonBadge } from '../../components/soon-badge/soon-badge';

/**
 * `/settings`: every settings group the user can open, split into company-wide and per-module setup.
 * Each group shows its description and a link per page. The search matches a group's name or
 * description (all its pages stay) or a page's name.
 */
export const SettingsOverviewPage = () => {
  const { t } = useTranslation();
  const { settingsSections } = usePermittedSidebar();
  const [query, setQuery] = useState('');

  const scopes = useMemo(() => {
    const q = query.trim().toLowerCase();
    const hits = (key: string) => t(key).toLowerCase().includes(q);
    const sections = settingsSections
      .map((section) => ({
        ...section,
        items:
          !q || hits(section.title) || hits(section.description)
            ? section.items
            : section.items.filter((item) => hits(item.name)),
      }))
      .filter((section) => section.items.length > 0);
    return SETTINGS_SCOPES.map((scope) => ({
      scope,
      sections: sections.filter((section) => section.scope === scope),
    })).filter((scope) => scope.sections.length > 0);
  }, [settingsSections, query, t]);

  return (
    <div className="flex w-full flex-col gap-6">
      <PageHeader title={t('SETTINGS')}>
        {/* Below 1024px the settings page picker above is searchable, so this box is wide screens only. */}
        <div className="relative w-56 max-lg:hidden">
          <MagnifyingGlass className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t('SEARCH_SETTINGS')}
            aria-label={t('SEARCH_SETTINGS')}
            className="pl-8"
          />
        </div>
      </PageHeader>

      {scopes.length === 0 && (
        <Card>
          <Empty>
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <MagnifyingGlass />
              </EmptyMedia>
              <EmptyTitle>{t('NO_SETTINGS_MATCH')}</EmptyTitle>
              <EmptyDescription>{t('NO_SETTINGS_MATCH_DESCRIPTION')}</EmptyDescription>
            </EmptyHeader>
          </Empty>
        </Card>
      )}

      {scopes.map(({ scope, sections }) => (
        <section key={scope} aria-labelledby={`settings-scope-${scope}`} className="grid gap-2">
          <h2
            id={`settings-scope-${scope}`}
            className="text-[13px] font-semibold text-muted-foreground"
          >
            {t(scope)}
          </h2>
          <Card className="divide-y divide-border overflow-hidden p-0">
            {sections.map((section) => (
              <div key={section.title} className="flex gap-3.5 p-4">
                <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-muted">
                  <MenuIcon name={section.icon as MenuIconName} size={18} />
                </span>
                <div className="min-w-0 flex-1">
                  <h3 className="font-semibold">{t(section.title)}</h3>
                  <p className="mt-0.5 text-sm text-muted-foreground">{t(section.description)}</p>
                  <div className="mt-2.5 flex flex-wrap gap-1.5">
                    {section.items.map((item) => (
                      <Link
                        key={item.id}
                        to={item.path}
                        className="inline-flex h-7 items-center gap-1.5 rounded-md border border-border px-2.5 text-[13px] font-medium transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                      >
                        {t(item.name)}
                        {item.soon && <SoonBadge />}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </Card>
        </section>
      ))}
    </div>
  );
};
